import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DBTable from '../handlers/DatabaseHandler';
import { userSchema } from '../schemas';
import { Card, Button, Input, Alert, Badge } from '../components/ui';
import { FiUser, FiMail, FiLock, FiPhone, FiUserPlus } from 'react-icons/fi';

// Country codes for telephone field
const countryCodes = [
    { code: '+852', country: 'Hong Kong', flag: '🇭🇰' },
    { code: '+1', country: 'United States', flag: '🇺🇸' },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+82', country: 'South Korea', flag: '🇰🇷' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬' },
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+39', country: 'Italy', flag: '🇮🇹' },
    { code: '+34', country: 'Spain', flag: '🇪🇸' },
    { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
    { code: '+46', country: 'Sweden', flag: '🇸🇪' },
    { code: '+47', country: 'Norway', flag: '🇳🇴' },
    { code: '+45', country: 'Denmark', flag: '🇩🇰' },
    { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
    { code: '+43', country: 'Austria', flag: '🇦🇹' },
    { code: '+32', country: 'Belgium', flag: '🇧🇪' }
];

/** UGC / Arts payment questions: HKU students, HKU staff, ATL general member — not Non-HKU community. */
function showPaymentEligibilityQuestions(role) {
    if (!role) return false;
    if (role === 'Non_ATL_General') return false;
    if (role.includes('HKU_Student') || role.includes('HKU_Staff')) return true;
    return role.includes('General');
}

/** HKU Staff / ATL HKU Staff — use staff-oriented UGC wording (not “enrolled in a programme”). */
function isHKUStaffRegistrationRole(role) {
    return !!(role && role.includes('HKU_Staff'));
}

function shouldRequirePayment(formData) {
    if (!showPaymentEligibilityQuestions(formData.User_Role)) return false;
    if (formData.isUGCFunded === null || formData.isFromArts === null) return false;
    // UGC-funded users should go through normal registration flow (no payment redirect).
    if (formData.isUGCFunded) return false;
    return true;
}

function Register() {
    const [formData, setFormData] = useState({
        Username: '',
        First_Name: '',
        Last_Name: '',
        Email_Address: '',
        Password: '',
        ConfirmPassword: '',
        Title: '',
        Gender: '',
        countryCode: '+852',
        Tel: '',
        User_Role: 'Non_ATL_General',
        ATL_Member: false,
        Member_ID: '',
        UID: '',
        direct_marketing: false,
        email_list: false,
        card_id: '',
        // New payment-related fields
        isUGCFunded: null, // null = not answered, true/false = answered
        isFromArts: null,  // null = not answered, true/false = answered
        createdAt: new Date(),
        lastLogin: new Date()
    });
    const [error, setError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const navigate = useNavigate();
    const userTable = new DBTable('USER', 'User_ID', userSchema);

    const validatePassword = (password) => {
        const requirements = {
            minLength: password.length >= 8,
            hasUppercase: /[A-Z]/.test(password),
            hasLowercase: /[a-z]/.test(password),
            hasNumber: /\d/.test(password),
            hasSpecial: /[@$!%*?&]/.test(password)
        };

        const errors = [];
        if (!requirements.minLength) errors.push('At least 8 characters');
        if (!requirements.hasUppercase) errors.push('One uppercase letter');
        if (!requirements.hasLowercase) errors.push('One lowercase letter');
        if (!requirements.hasNumber) errors.push('One number');
        if (!requirements.hasSpecial) errors.push('One special character (@$!%*?&)');

        return {
            isValid: Object.values(requirements).every(Boolean),
            errors,
            requirements
        };
    };

    const validateRequiredFields = () => {
        const errors = {};
        const requiredFields = ['Username', 'First_Name', 'Last_Name', 'Email_Address', 'Gender', 'Password'];
        
        requiredFields.forEach(field => {
            if (!formData[field] || formData[field].toString().trim() === '') {
                errors[field] = `${field.replace('_', ' ')} is required`;
            }
        });
        
        // Special validation for email format
        if (formData.Email_Address) {
            const isHKUStudent = formData.User_Role && formData.User_Role.includes('HKU_Student');
            const isHKUStaff = formData.User_Role && formData.User_Role.includes('HKU_Staff');
            
            if (isHKUStudent) {
                // For HKU students, must end with @connect.hku.hk
                if (!formData.Email_Address.endsWith('@connect.hku.hk')) {
                    errors.Email_Address = 'HKU students must use @connect.hku.hk email domain';
                } else {
                    // Validate local part (username part before @)
                    const localPart = formData.Email_Address.replace('@connect.hku.hk', '');
                    if (!localPart || localPart.trim() === '') {
                        errors.Email_Address = 'Please enter your HKU username';
                    }
                }
            } else if (isHKUStaff) {
                // For HKU staff, must end with @hku.hk
                if (!formData.Email_Address.endsWith('@hku.hk')) {
                    errors.Email_Address = 'HKU staff must use @hku.hk email domain';
                } else {
                    // Validate local part (username part before @)
                    const localPart = formData.Email_Address.replace('@hku.hk', '');
                    if (!localPart || localPart.trim() === '') {
                        errors.Email_Address = 'Please enter your HKU email username';
                    }
                }
            } else {
                // For non-HKU users, standard email validation
                if (!/\S+@\S+\.\S+/.test(formData.Email_Address)) {
                    errors.Email_Address = 'Please enter a valid email address';
                }
            }
        }
        
        // Username validation
        if (formData.Username && formData.Username.length < 3) {
            errors.Username = 'Username must be at least 3 characters long';
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        
        // Handle role change - reset email if switching to/from HKU student or staff
        if (name === 'User_Role') {
            const isHKUStudent = newValue.includes('HKU_Student');
            const isHKUStaff = newValue.includes('HKU_Staff');
            const wasHKUStudent = formData.User_Role && formData.User_Role.includes('HKU_Student');
            const wasHKUStaff = formData.User_Role && formData.User_Role.includes('HKU_Staff');
            
            setFormData(prev => {
                const updated = {
                    ...prev,
                    [name]: newValue
                };
                
                // If switching to HKU student, ensure email ends with @connect.hku.hk
                if (isHKUStudent && !wasHKUStudent) {
                    const currentEmail = prev.Email_Address;
                    if (currentEmail && !currentEmail.endsWith('@connect.hku.hk')) {
                        // Extract local part if it has a different domain
                        const localPart = currentEmail.split('@')[0];
                        updated.Email_Address = localPart ? `${localPart}@connect.hku.hk` : '';
                    } else if (!currentEmail) {
                        updated.Email_Address = '';
                    }
                }
                // If switching to HKU staff, ensure email ends with @hku.hk
                else if (isHKUStaff && !wasHKUStaff) {
                    const currentEmail = prev.Email_Address;
                    if (currentEmail && !currentEmail.endsWith('@hku.hk')) {
                        // Extract local part if it has a different domain
                        const localPart = currentEmail.split('@')[0];
                        updated.Email_Address = localPart ? `${localPart}@hku.hk` : '';
                    } else if (!currentEmail) {
                        updated.Email_Address = '';
                    }
                }
                // If switching away from HKU student/staff, remove domain restriction
                else if ((!isHKUStudent && wasHKUStudent) || (!isHKUStaff && wasHKUStaff)) {
                    const currentEmail = prev.Email_Address;
                    if (currentEmail) {
                        if (currentEmail.endsWith('@connect.hku.hk')) {
                            updated.Email_Address = currentEmail.replace('@connect.hku.hk', '');
                        } else if (currentEmail.endsWith('@hku.hk')) {
                            updated.Email_Address = currentEmail.replace('@hku.hk', '');
                        }
                    }
                }
                
                if (newValue === 'Non_ATL_General') {
                    updated.isUGCFunded = null;
                    updated.isFromArts = null;
                }
                return updated;
            });
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: newValue
            }));
        }
        
        // Clear validation error for this field when user starts typing
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }

        // Validate password when it changes
        if (name === 'Password') {
            const { isValid, errors, requirements } = validatePassword(value);
            setPasswordError(isValid ? '' : `Password must contain: ${errors.join(', ')}`);
            
            // Update password requirements display
            setPasswordRequirements({
                minLength: requirements.minLength,
                hasUppercase: requirements.hasUppercase,
                hasLowercase: requirements.hasLowercase,
                hasNumber: requirements.hasNumber,
                hasSpecial: requirements.hasSpecial
            });
        }
    };

    // Add state for password requirements
    const [passwordRequirements, setPasswordRequirements] = useState({
        minLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecial: false
    });

    const validateForm = () => {
        // Validate required fields first
        if (!validateRequiredFields()) {
            setError('Please fill in all required fields correctly');
            return false;
        }
        
        if (showPaymentEligibilityQuestions(formData.User_Role)) {
            if (formData.isUGCFunded === null) {
                setError(
                    isHKUStaffRegistrationRole(formData.User_Role)
                        ? 'Please answer whether your HKU role is UGC-funded'
                        : 'Please answer if you are in a UGC-funded program'
                );
                return false;
            }
            if (formData.isFromArts === null) {
                setError(
                    isHKUStaffRegistrationRole(formData.User_Role)
                        ? 'Please answer whether Faculty of Arts is your home faculty'
                        : 'Please answer if you are from Faculty of Arts'
                );
                return false;
            }
        }
        
        // Validate password
        const { isValid, errors } = validatePassword(formData.Password);
        if (!isValid) {
            setError(`Password must contain: ${errors.join(', ')}`);
            return false;
        }

        if (formData.Password !== formData.ConfirmPassword) {
            setError('Passwords do not match');
            return false;
        }

        // Validate ATL Member ID if user is ATL member
        if (formData.ATL_Member && !formData.Member_ID.trim()) {
            setError('ATL Member ID is required for ATL members');
            return false;
        }

        // Validate UID for non-ATL HKU Staff and Students
        const hkuRoles = ['Non_ATL_HKU_Staff', 'Non_ATL_HKU_Student'];
        if (hkuRoles.includes(formData.User_Role) && !formData.UID.trim()) {
            setError('HKU UID is required for HKU Staff and Students');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        try {
            // Regular registration - create unapproved account
            const { ConfirmPassword, countryCode, ...userData } = formData;
            // Combine country code with phone number
            userData.Tel = `${formData.countryCode}${formData.Tel}`;
            // Use Username as User_ID
            userData.User_ID = formData.Username;
            // Set approved to false for new registrations
            userData.approved = false;

            const response = await fetch('/api/database', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    collection: 'USER',
                    ID: 'User_ID',
                    row: userData
                }),
            });

            const data = await response.json();

            if (response.ok) {
                localStorage.setItem('pendingVerificationEmail', formData.Email_Address);
                navigate(`/verify-email?email=${encodeURIComponent(formData.Email_Address)}`);
            } else {
                setError(data.message || 'Failed to create account. Please try again.');
            }
        } catch (error) {
            setError('An error occurred. Please try again.');
            console.error('Registration error:', error);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <Card className="max-w-2xl w-full">
                <Card.Header className="text-center">
                    <Card.Title className="text-3xl font-elegant text-primary-900 dark:text-white">
                        Create your account
                    </Card.Title>
                    <Card.Description className="font-literary text-primary-600 dark:text-gray-300">
                        Fields marked with <span className="text-error-500 font-medium">*</span> are required
                    </Card.Description>
                    
                    {/* Policy Announcement */}
                    <div className="mt-4 p-4 bg-primary-100 dark:bg-gray-700 border-l-4 border-primary-600 dark:border-primary-400 rounded-r-lg">
                        <div className="flex items-start gap-3">
                            <span className="text-2xl">ℹ️</span>
                            <div>
                                <h4 className="text-sm font-serif font-semibold text-primary-900 dark:text-white mb-1">
                                    Portal Access Policy
                                </h4>
                                <p className="text-sm font-literary text-primary-700 dark:text-gray-300">
                                    This portal is primarily open to <strong>HKU Faculty of Arts students enrolled in UGC-funded programs</strong>. 
                                    For outside users, non-UGC funded students, or students from other faculties, membership functions will be available soon.
                                </p>
                            </div>
                        </div>
                    </div>
                </Card.Header>
                <Card.Content>
                <form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-6">
                        <Input
                            label="Username"
                            name="Username"
                            type="text"
                            required
                            placeholder="Enter your username (min 3 characters)"
                            value={formData.Username}
                            onChange={handleChange}
                            icon={FiUser}
                            error={validationErrors.Username}
                            helperText="Must be at least 3 characters long, letters and numbers only"
                        />
                        
                        <div>
                            <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">Role</label>
                            <select
                                id="User_Role"
                                name="User_Role"
                                className="w-full px-4 py-3 font-literary border border-primary-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/20 dark:focus:ring-primary-400/20 focus:border-primary-500 dark:focus:border-primary-400 transition-all duration-300"
                                value={formData.User_Role}
                                onChange={handleChange}
                            >
                                {formData.ATL_Member ? (
                                    <>
                                        <option value="ATL_Member_HKU_Staff">HKU Staff (ATL Member)</option>
                                        <option value="ATL_Member_HKU_Student">HKU Student (ATL Member)</option>
                                        <option value="ATL_Member_General">General User (ATL Member)</option>
                                    </>
                                ) : (
                                    <>
                                        <option value="Non_ATL_HKU_Staff">HKU Staff</option>
                                        <option value="Non_ATL_HKU_Student">HKU Student</option>
                                        <option value="Non_ATL_General">Non-HKU community</option>
                                    </>
                                )}
                            </select>
                        </div>
                        
                        <Input
                            label="First Name"
                            name="First_Name"
                            type="text"
                            required
                            placeholder="Enter your first name"
                            value={formData.First_Name}
                            onChange={handleChange}
                            icon={FiUser}
                            error={validationErrors.First_Name}
                        />
                        <Input
                            label="Last Name"
                            name="Last_Name"
                            type="text"
                            required
                            placeholder="Enter your last name"
                            value={formData.Last_Name}
                            onChange={handleChange}
                            icon={FiUser}
                            error={validationErrors.Last_Name}
                        />
                        <div>
                            <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                Title
                            </label>
                            <select
                                id="Title"
                                name="Title"
                                className={`w-full px-4 py-3 font-literary border rounded-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white placeholder-primary-400 dark:placeholder-gray-500 transition-all duration-300 ${
                                    validationErrors.Title 
                                        ? 'border-error-300 dark:border-error-600 focus:border-error-500 focus:ring-error-500/20' 
                                        : 'border-primary-300 dark:border-gray-600 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-primary-500/20 dark:focus:ring-primary-400/20'
                                } focus:outline-none focus:ring-4`}
                                value={formData.Title}
                                onChange={handleChange}
                            >
                                <option value="">Select Title</option>
                                <option value="Mr.">Mr.</option>
                                <option value="Ms.">Ms.</option>
                                <option value="Dr.">Dr.</option>
                                <option value="Prof.">Prof.</option>
                            </select>
                            {validationErrors.Title && (
                                <p className="mt-1 text-sm font-sans text-error-600 dark:text-error-400">{validationErrors.Title}</p>
                            )}
                        </div>
                        {/* Email Address - with HKU domain restriction for HKU students and staff */}
                        {formData.User_Role && formData.User_Role.includes('HKU_Student') ? (
                            <div>
                                <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                    HKU Email <span className="text-error-500">*</span>
                                </label>
                                <div className="flex">
                                    <input
                                        type="text"
                                        name="Email_Address"
                                        required
                                        placeholder="Enter your HKU username"
                                        value={formData.Email_Address.replace('@connect.hku.hk', '')}
                                        onChange={(e) => {
                                            const localPart = e.target.value.replace(/@.*$/, ''); // Remove any @domain if user types it
                                            setFormData(prev => ({
                                                ...prev,
                                                Email_Address: localPart ? `${localPart}@connect.hku.hk` : ''
                                            }));
                                            // Clear validation error when user starts typing
                                            if (validationErrors.Email_Address) {
                                                setValidationErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.Email_Address;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        className={`flex-1 px-4 py-3 font-literary border border-r-0 rounded-l-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white placeholder-primary-400 dark:placeholder-gray-500 transition-all duration-300 ${
                                            validationErrors.Email_Address 
                                                ? 'border-error-300 dark:border-error-600 focus:border-error-500 focus:ring-error-500/20' 
                                                : 'border-primary-300 dark:border-gray-600 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-primary-500/20 dark:focus:ring-primary-400/20'
                                        } focus:outline-none focus:ring-4`}
                                    />
                                    <div className="px-4 py-3 border border-l-0 border-primary-300 dark:border-gray-600 bg-primary-50 dark:bg-gray-700 text-primary-700 dark:text-gray-300 rounded-r-lg font-literary flex items-center">
                                        @connect.hku.hk
                                    </div>
                                </div>
                                {validationErrors.Email_Address && (
                                    <p className="mt-1 text-sm font-sans text-error-600 dark:text-error-400">{validationErrors.Email_Address}</p>
                                )}
                                <p className="mt-2 text-xs font-sans text-primary-500 dark:text-gray-400">
                                    Enter your HKU username (e.g., u3658833)
                                </p>
                            </div>
                        ) : formData.User_Role && formData.User_Role.includes('HKU_Staff') ? (
                            <div>
                                <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                    HKU Email <span className="text-error-500">*</span>
                                </label>
                                <div className="flex">
                                    <input
                                        type="text"
                                        name="Email_Address"
                                        required
                                        placeholder="Enter your HKU email username"
                                        value={formData.Email_Address.replace('@hku.hk', '')}
                                        onChange={(e) => {
                                            const localPart = e.target.value.replace(/@.*$/, ''); // Remove any @domain if user types it
                                            setFormData(prev => ({
                                                ...prev,
                                                Email_Address: localPart ? `${localPart}@hku.hk` : ''
                                            }));
                                            // Clear validation error when user starts typing
                                            if (validationErrors.Email_Address) {
                                                setValidationErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.Email_Address;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        className={`flex-1 px-4 py-3 font-literary border border-r-0 rounded-l-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white placeholder-primary-400 dark:placeholder-gray-500 transition-all duration-300 ${
                                            validationErrors.Email_Address 
                                                ? 'border-error-300 dark:border-error-600 focus:border-error-500 focus:ring-error-500/20' 
                                                : 'border-primary-300 dark:border-gray-600 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-primary-500/20 dark:focus:ring-primary-400/20'
                                        } focus:outline-none focus:ring-4`}
                                    />
                                    <div className="px-4 py-3 border border-l-0 border-primary-300 dark:border-gray-600 bg-primary-50 dark:bg-gray-700 text-primary-700 dark:text-gray-300 rounded-r-lg font-literary flex items-center">
                                        @hku.hk
                                    </div>
                                </div>
                                {validationErrors.Email_Address && (
                                    <p className="mt-1 text-sm font-sans text-error-600 dark:text-error-400">{validationErrors.Email_Address}</p>
                                )}
                                <p className="mt-2 text-xs font-sans text-primary-500 dark:text-gray-400">
                                    Enter your HKU email username (e.g., john.smith)
                                </p>
                            </div>
                        ) : (
                            <Input
                                label="Email Address"
                                name="Email_Address"
                                type="email"
                                required
                                placeholder="Enter your email address (e.g., user@example.com)"
                                value={formData.Email_Address}
                                onChange={handleChange}
                                icon={FiMail}
                                error={validationErrors.Email_Address}
                                helperText="Must be a valid email format (e.g., user@example.com)"
                            />
                        )}
                        <div>
                            <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                Gender <span className="text-error-500">*</span>
                            </label>
                            <select
                                id="Gender"
                                name="Gender"
                                required
                                className={`w-full px-4 py-3 font-literary border rounded-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white placeholder-primary-400 dark:placeholder-gray-500 transition-all duration-300 ${
                                    validationErrors.Gender 
                                        ? 'border-error-300 dark:border-error-600 focus:border-error-500 focus:ring-error-500/20' 
                                        : 'border-primary-300 dark:border-gray-600 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-primary-500/20 dark:focus:ring-primary-400/20'
                                } focus:outline-none focus:ring-4`}
                                value={formData.Gender}
                                onChange={handleChange}
                            >
                                <option value="">Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                                <option value="Prefer not to say">Prefer not to say</option>
                            </select>
                            {validationErrors.Gender && (
                                <p className="mt-1 text-sm font-sans text-error-600 dark:text-error-400">{validationErrors.Gender}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                Telephone
                            </label>
                            <div className="flex">
                                <select
                                    name="countryCode"
                                    className="appearance-none block px-4 py-3 border border-primary-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-primary-900 dark:text-white rounded-l-lg focus:outline-none focus:ring-4 focus:ring-primary-500/20 dark:focus:ring-primary-400/20 focus:border-primary-500 dark:focus:border-primary-400 font-literary transition-all duration-300"
                                    value={formData.countryCode}
                                    onChange={handleChange}
                                    style={{ minWidth: '120px' }}
                                >
                                    {countryCodes.map((country) => (
                                        <option key={country.code} value={country.code}>
                                            {country.flag} {country.code}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    id="Tel"
                                    name="Tel"
                                    type="tel"
                                    placeholder="Enter your phone number"
                                    className="appearance-none flex-1 block w-full px-4 py-3 border border-l-0 border-primary-300 dark:border-gray-600 bg-white dark:bg-gray-800 placeholder-primary-400 dark:placeholder-gray-500 text-primary-900 dark:text-white rounded-r-lg focus:outline-none focus:ring-4 focus:ring-primary-500/20 dark:focus:ring-primary-400/20 focus:border-primary-500 dark:focus:border-primary-400 font-literary transition-all duration-300"
                                    value={formData.Tel}
                                    onChange={handleChange}
                                />
                            </div>
                            <p className="mt-2 text-xs font-sans text-primary-500 dark:text-gray-400">
                                Format: {formData.countryCode === '+852' ? '12345678 (8 digits, Hong Kong)' : 
                                        formData.countryCode === '+1' ? '1234567890 (10 digits, US/Canada)' : 
                                        formData.countryCode === '+44' ? '1234567890 (10-11 digits, UK)' : 
                                        'Enter digits only (no spaces or dashes)'}
                            </p>
                        </div>
                        <div>
                            <Input
                                label="Password"
                                name="Password"
                                type="password"
                                required
                                value={formData.Password}
                                onChange={handleChange}
                                icon={FiLock}
                                error={passwordError || validationErrors.Password}
                                showPasswordToggle
                            />
                            <div className="mt-3 p-4 bg-primary-50 dark:bg-gray-700 rounded-lg">
                                <p className="font-serif font-medium text-primary-900 dark:text-white mb-2">Password Requirements:</p>
                                <ul className="space-y-1">
                                    <li className={`flex items-center font-literary text-sm ${
                                        passwordRequirements.minLength ? 'text-success-600 dark:text-success-400' : 'text-primary-500 dark:text-gray-400'
                                    }`}>
                                        <span className="mr-2">{passwordRequirements.minLength ? '✓' : '○'}</span>
                                        At least 8 characters
                                    </li>
                                    <li className={`flex items-center font-literary text-sm ${
                                        passwordRequirements.hasUppercase ? 'text-success-600 dark:text-success-400' : 'text-primary-500 dark:text-gray-400'
                                    }`}>
                                        <span className="mr-2">{passwordRequirements.hasUppercase ? '✓' : '○'}</span>
                                        One uppercase letter (A-Z)
                                    </li>
                                    <li className={`flex items-center font-literary text-sm ${
                                        passwordRequirements.hasLowercase ? 'text-success-600 dark:text-success-400' : 'text-primary-500 dark:text-gray-400'
                                    }`}>
                                        <span className="mr-2">{passwordRequirements.hasLowercase ? '✓' : '○'}</span>
                                        One lowercase letter (a-z)
                                    </li>
                                    <li className={`flex items-center font-literary text-sm ${
                                        passwordRequirements.hasNumber ? 'text-success-600 dark:text-success-400' : 'text-primary-500 dark:text-gray-400'
                                    }`}>
                                        <span className="mr-2">{passwordRequirements.hasNumber ? '✓' : '○'}</span>
                                        One number (0-9)
                                    </li>
                                    <li className={`flex items-center font-literary text-sm ${
                                        passwordRequirements.hasSpecial ? 'text-success-600 dark:text-success-400' : 'text-primary-500 dark:text-gray-400'
                                    }`}>
                                        <span className="mr-2">{passwordRequirements.hasSpecial ? '✓' : '○'}</span>
                                        One special character (@$!%*?&)
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <Input
                            label="Confirm Password"
                            name="ConfirmPassword"
                            type="password"
                            required
                            value={formData.ConfirmPassword}
                            onChange={handleChange}
                            icon={FiLock}
                            error={formData.Password !== formData.ConfirmPassword ? 'Passwords do not match' : ''}
                            showPasswordToggle
                        />
                        <div>
                            <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-4">ATL Membership</label>
                            <div className="space-y-3">
                                        <label className="flex items-center p-3 border border-primary-200 dark:border-gray-600 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                                            <input
                                                type="radio"
                                                name="ATL_Member"
                                                value="true"
                                                checked={formData.ATL_Member === true}
                                                onChange={() => {
                                                    const wasHKUStudent = formData.User_Role && formData.User_Role.includes('HKU_Student');
                                                    setFormData(prev => {
                                                        const updated = {
                                                            ...prev,
                                                            ATL_Member: true,
                                                            // Reset role to first ATL option if current role is non-ATL
                                                            User_Role: prev.User_Role.startsWith('ATL_') ? prev.User_Role : 'ATL_Member_HKU_Staff'
                                                        };
                                                        // If switching to ATL and was HKU student, maintain HKU student role
                                                        if (wasHKUStudent) {
                                                            updated.User_Role = 'ATL_Member_HKU_Student';
                                                        }
                                                        return updated;
                                                    });
                                                }}
                                                className="mr-3 text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="font-literary text-primary-900 dark:text-white">ATL Member</span>
                                        </label>
                                        <label className="flex items-center p-3 border border-primary-200 dark:border-gray-600 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                                            <input
                                                type="radio"
                                                name="ATL_Member"
                                                value="false"
                                                checked={formData.ATL_Member === false}
                                                onChange={() => {
                                                    const wasHKUStudent = formData.User_Role && formData.User_Role.includes('HKU_Student');
                                                    setFormData(prev => {
                                                        const updated = {
                                                            ...prev,
                                                            ATL_Member: false,
                                                            Member_ID: '', // Clear member ID when switching to non-ATL
                                                            // Reset role to first non-ATL option if current role is ATL
                                                            User_Role: prev.User_Role.startsWith('Non_ATL_') ? prev.User_Role : 'Non_ATL_HKU_Staff'
                                                        };
                                                        // If switching to non-ATL and was HKU student, maintain HKU student role
                                                        if (wasHKUStudent) {
                                                            updated.User_Role = 'Non_ATL_HKU_Student';
                                                        }
                                                        return updated;
                                                    });
                                                }}
                                                className="mr-3 text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="font-literary text-primary-900 dark:text-white">Non-ATL Member</span>
                                        </label>
                            </div>

                            {formData.ATL_Member && (
                                    <Input
                                        label="ATL Member ID"
                                        name="Member_ID"
                                        type="text"
                                        required
                                        placeholder="Enter your ATL Member ID (e.g., ATL2024001)"
                                        value={formData.Member_ID}
                                        onChange={handleChange}
                                        helperText="Must be a valid ATL Member ID (e.g., ATL2024001)"
                                    />
                            )}

                            {/* HKU UID is only required for non-ATL HKU Staff/Students */}
                            {['Non_ATL_HKU_Staff', 'Non_ATL_HKU_Student'].includes(formData.User_Role) && (
                                    <Input
                                        label="HKU UID"
                                        name="UID"
                                        type="text"
                                        required
                                        placeholder="Enter your HKU UID (e.g., 3031234567)"
                                        value={formData.UID}
                                        onChange={handleChange}
                                        helperText="Must be a valid HKU UID (10 digits, e.g., 3031234567)"
                                    />
                            )}

                            {/* Payment Eligibility — HKU student/staff + ATL general; hidden for Non-HKU community */}
                            {showPaymentEligibilityQuestions(formData.User_Role) && (
                                    <div className="border-t border-primary-200 dark:border-gray-700 pt-6 mt-6">
                                        <h3 className="text-lg font-serif font-medium text-primary-900 dark:text-white mb-4 flex items-center gap-2">
                                            <span>💳</span> Payment Eligibility
                                        </h3>
                                        <p className="text-sm font-literary text-primary-600 dark:text-gray-400 mb-6">
                                            {isHKUStaffRegistrationRole(formData.User_Role)
                                                ? 'As HKU staff, please answer the following to determine if payment is required for registration (same UGC / Faculty of Arts criteria as for students).'
                                                : 'Please answer the following questions to determine if payment is required for registration.'}
                                        </p>

                                        {/* UGC-funded: student vs staff wording */}
                                        <div className="mb-6">
                                            <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-3">
                                                {isHKUStaffRegistrationRole(formData.User_Role) ? (
                                                    <>Is your HKU appointment or role UGC-funded? <span className="text-error-500">*</span></>
                                                ) : (
                                                    <>Are you enrolled in a UGC-funded program? <span className="text-error-500">*</span></>
                                                )}
                                            </label>
                                            <div className="space-y-3">
                                                <div className="flex items-start p-4 border-2 border-primary-200 dark:border-gray-600 rounded-lg hover:border-primary-400 dark:hover:border-gray-500 transition-colors cursor-pointer"
                                                    onClick={() => setFormData(prev => ({ ...prev, isUGCFunded: true }))}
                                                >
                                                    <div className="flex items-center h-5 mt-0.5">
                                                        <input
                                                            type="radio"
                                                            name="isUGCFunded"
                                                            checked={formData.isUGCFunded === true}
                                                            onChange={() => setFormData(prev => ({ ...prev, isUGCFunded: true }))}
                                                            className="focus:ring-primary-500 dark:focus:ring-primary-400 h-4 w-4 text-primary-600 border-primary-300 dark:border-gray-600"
                                                        />
                                                    </div>
                                                    <div className="ml-3">
                                                        <label className="font-serif font-medium text-primary-900 dark:text-white cursor-pointer">
                                                            {isHKUStaffRegistrationRole(formData.User_Role)
                                                                ? 'Yes — my post or role is UGC-funded'
                                                                : 'Yes, I am in a UGC-funded program'}
                                                        </label>
                                                        <p className="font-literary text-sm text-primary-600 dark:text-gray-400 mt-1">
                                                            {isHKUStaffRegistrationRole(formData.User_Role)
                                                                ? '(e.g., substantive teaching / research posts funded through the University Grants Committee)'
                                                                : '(e.g., BA, BSc, BBA, BEng programs funded by University Grants Committee)'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-start p-4 border-2 border-primary-200 dark:border-gray-600 rounded-lg hover:border-primary-400 dark:hover:border-gray-500 transition-colors cursor-pointer"
                                                    onClick={() => setFormData(prev => ({ ...prev, isUGCFunded: false }))}
                                                >
                                                    <div className="flex items-center h-5 mt-0.5">
                                                        <input
                                                            type="radio"
                                                            name="isUGCFunded"
                                                            checked={formData.isUGCFunded === false}
                                                            onChange={() => setFormData(prev => ({ ...prev, isUGCFunded: false }))}
                                                            className="focus:ring-primary-500 dark:focus:ring-primary-400 h-4 w-4 text-primary-600 border-primary-300 dark:border-gray-600"
                                                        />
                                                    </div>
                                                    <div className="ml-3">
                                                        <label className="font-serif font-medium text-primary-900 dark:text-white cursor-pointer">
                                                            {isHKUStaffRegistrationRole(formData.User_Role)
                                                                ? 'No — my post or role is not UGC-funded'
                                                                : 'No, I am in a non-UGC-funded program'}
                                                        </label>
                                                        <p className="font-literary text-sm text-primary-600 dark:text-gray-400 mt-1">
                                                            {isHKUStaffRegistrationRole(formData.User_Role)
                                                                ? '(e.g., contract or project posts, self-financing units, or roles not supported by UGC)'
                                                                : '(e.g., Associate Degree, SPACE programs, self-funded programs)'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Faculty of Arts Question */}
                                        <div className="mb-6">
                                            <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-3">
                                                {isHKUStaffRegistrationRole(formData.User_Role) ? (
                                                    <>Is the Faculty of Arts your home faculty? <span className="text-error-500">*</span></>
                                                ) : (
                                                    <>Are you from the Faculty of Arts? <span className="text-error-500">*</span></>
                                                )}
                                            </label>
                                            <div className="space-y-3">
                                                <div className="flex items-start p-4 border-2 border-primary-200 dark:border-gray-600 rounded-lg hover:border-primary-400 dark:hover:border-gray-500 transition-colors cursor-pointer"
                                                    onClick={() => setFormData(prev => ({ ...prev, isFromArts: true }))}
                                                >
                                                    <div className="flex items-center h-5 mt-0.5">
                                                        <input
                                                            type="radio"
                                                            name="isFromArts"
                                                            checked={formData.isFromArts === true}
                                                            onChange={() => setFormData(prev => ({ ...prev, isFromArts: true }))}
                                                            className="focus:ring-primary-500 dark:focus:ring-primary-400 h-4 w-4 text-primary-600 border-primary-300 dark:border-gray-600"
                                                        />
                                                    </div>
                                                    <div className="ml-3">
                                                        <label className="font-serif font-medium text-primary-900 dark:text-white cursor-pointer">
                                                            {isHKUStaffRegistrationRole(formData.User_Role)
                                                                ? 'Yes — Faculty of Arts is my home faculty'
                                                                : 'Yes, I am from Faculty of Arts'}
                                                        </label>
                                                        <p className="font-literary text-sm text-primary-600 dark:text-gray-400 mt-1">
                                                            {isHKUStaffRegistrationRole(formData.User_Role)
                                                                ? '(Faculty code: 01 — includes academic and professional staff)'
                                                                : '(Faculty code: 01)'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-start p-4 border-2 border-primary-200 dark:border-gray-600 rounded-lg hover:border-primary-400 dark:hover:border-gray-500 transition-colors cursor-pointer"
                                                    onClick={() => setFormData(prev => ({ ...prev, isFromArts: false }))}
                                                >
                                                    <div className="flex items-center h-5 mt-0.5">
                                                        <input
                                                            type="radio"
                                                            name="isFromArts"
                                                            checked={formData.isFromArts === false}
                                                            onChange={() => setFormData(prev => ({ ...prev, isFromArts: false }))}
                                                            className="focus:ring-primary-500 dark:focus:ring-primary-400 h-4 w-4 text-primary-600 border-primary-300 dark:border-gray-600"
                                                        />
                                                    </div>
                                                    <div className="ml-3">
                                                        <label className="font-serif font-medium text-primary-900 dark:text-white cursor-pointer">
                                                            {isHKUStaffRegistrationRole(formData.User_Role)
                                                                ? 'No — my home faculty is not Arts'
                                                                : 'No, I am from another faculty'}
                                                        </label>
                                                        <p className="font-literary text-sm text-primary-600 dark:text-gray-400 mt-1">
                                                            (e.g., Engineering, Science, Business, Law, etc.)
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Payment Status Alert */}
                                        {formData.isUGCFunded !== null && formData.isFromArts !== null && (
                                            <div className={`p-4 rounded-lg border-2 ${
                                                !shouldRequirePayment(formData)
                                                    ? 'bg-success-50 border-success-300 dark:bg-success-900/20 dark:border-success-700'
                                                    : 'bg-warning-50 border-warning-300 dark:bg-warning-900/20 dark:border-warning-700'
                                            }`}>
                                                <div className="flex items-start gap-3">
                                                    <span className="text-2xl">
                                                        {!shouldRequirePayment(formData) ? '✅' : '💳'}
                                                    </span>
                                                    <div>
                                                        <h4 className={`font-serif font-semibold ${
                                                            !shouldRequirePayment(formData)
                                                                ? 'text-success-900 dark:text-success-200'
                                                                : 'text-warning-900 dark:text-warning-200'
                                                        }`}>
                                                            {!shouldRequirePayment(formData)
                                                                ? 'No Payment Required'
                                                                : 'Payment Required'
                                                            }
                                                        </h4>
                                                        <p className={`text-sm font-literary mt-1 ${
                                                            !shouldRequirePayment(formData)
                                                                ? 'text-success-700 dark:text-success-300'
                                                                : 'text-warning-700 dark:text-warning-300'
                                                        }`}>
                                                            {!shouldRequirePayment(formData)
                                                                ? 'You are eligible for free registration. You can submit your registration directly through the normal account creation flow. Admin approval is still required.'
                                                                : 'Please complete payment before your account can be approved. You will be redirected to the HKU payment system.'
                                                            }
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                            )}
                        </div>

                        {/* Marketing Preferences */}
                        <div className="border-t border-primary-200 dark:border-gray-700 pt-6">
                            <h3 className="text-lg font-serif font-medium text-primary-900 dark:text-white mb-4">Marketing Preferences</h3>
                            <div className="space-y-4">
                                <div className="flex items-start p-4 border border-primary-200 dark:border-gray-600 rounded-lg">
                                    <div className="flex items-center h-5 mt-0.5">
                                        <input
                                            id="direct_marketing"
                                            name="direct_marketing"
                                            type="checkbox"
                                            checked={formData.direct_marketing}
                                            onChange={handleChange}
                                            className="focus:ring-primary-500 dark:focus:ring-primary-400 h-4 w-4 text-primary-600 border-primary-300 dark:border-gray-600 rounded"
                                        />
                                    </div>
                                    <div className="ml-3">
                                        <label htmlFor="direct_marketing" className="font-serif font-medium text-primary-900 dark:text-white">
                                            Direct Marketing
                                        </label>
                                        <p className="font-literary text-primary-600 dark:text-gray-300 mt-1">I agree to receive direct marketing communications from ATL</p>
                                    </div>
                                </div>
                                <div className="flex items-start p-4 border border-primary-200 dark:border-gray-600 rounded-lg">
                                    <div className="flex items-center h-5 mt-0.5">
                                        <input
                                            id="email_list"
                                            name="email_list"
                                            type="checkbox"
                                            checked={formData.email_list}
                                            onChange={handleChange}
                                            className="focus:ring-primary-500 dark:focus:ring-primary-400 h-4 w-4 text-primary-600 border-primary-300 dark:border-gray-600 rounded"
                                        />
                                    </div>
                                    <div className="ml-3">
                                        <label htmlFor="email_list" className="font-serif font-medium text-primary-900 dark:text-white">
                                            Email List
                                        </label>
                                        <p className="font-literary text-primary-600 dark:text-gray-300 mt-1">I agree to receive email newsletters and updates from ATL</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <Alert type="error">
                            {error}
                        </Alert>
                    )}

                    {/* Conditional Button based on payment eligibility */}
                    {showPaymentEligibilityQuestions(formData.User_Role) &&
                     formData.isUGCFunded !== null && formData.isFromArts !== null &&
                     shouldRequirePayment(formData) ? (
                        // Payment Required Button
                        <div className="space-y-3">
                            <Button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (validateForm()) {
                                        // Placeholder payment URL - will be replaced later
                                        const paymentURL = 'https://hkufinance.hku.hk/payment';
                                        // Store form data in sessionStorage before redirecting
                                        sessionStorage.setItem('registrationData', JSON.stringify(formData));
                                        // Redirect to HKU payment system
                                        window.location.href = paymentURL;
                                    }
                                }}
                                variant="warning"
                                size="lg"
                                className="w-full bg-warning-600 hover:bg-warning-700 border-transparent"
                            >
                                <span className="mr-2">💳</span>
                                Proceed to Payment
                            </Button>
                            <p className="text-xs text-center font-literary text-primary-600 dark:text-gray-400">
                                You will be redirected to HKU payment system. After payment, your account will be submitted for admin approval.
                            </p>
                        </div>
                    ) : (
                        // Normal Submit Button
                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="w-full"
                            icon={FiUserPlus}
                        >
                            Create Account
                        </Button>
                    )}
                </form>
                </Card.Content>
                <Card.Footer className="text-center">
                    <p className="font-literary text-primary-600 dark:text-gray-400">
                        Already have an account?{' '}
                        <Link to="/login" className="font-serif text-primary-700 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 transition-colors">
                            Sign in here
                        </Link>
                    </p>
                </Card.Footer>
            </Card>
        </div>
    );
}

export default Register; 